/* eslint-disable react/jsx-no-bind -- MUI pagination hands back (event, newPage) and has to be adapted to handlePageChange's single argument */
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Pagination from '@mui/material/Pagination'
import PropTypes from 'prop-types'
import React, { useState, Children, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { useLazyFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import HHTablePagination from 'components/common/HHTablePagination'
import { DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC } from 'components/customer-details/content/work-orders/settings'
import { buildWorkOrderListFilters } from 'components/customer-details/content/work-orders/utils'
import { MONTHS } from 'settings/constants/date'
import { CUSTOMER_DETAILS_DEFAULT_PAGINATION } from 'settings/constants/pagination'
import { formatDateToBEFormat } from 'utils/date'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'
import { memo } from 'utils/react'

import WorkOrderListItem from './WorkOrderListItem'

const { INITIAL_PAGE, ROWS_PER_PAGE_OPTIONS } = CUSTOMER_DETAILS_DEFAULT_PAGINATION
const ROWS_PER_PAGE = 25

const WorkOrderList = ({
  accountId,
  locationId,
  configuredServiceId,
  isExpanded = false,
  year,
  month,
  hasFilters = false,
  disposalSites = [],
}) => {
  const [fetchWorkOrderList, { isFetching: isLoading, data }] = useLazyFetchWorkOrderListQuery()
  const [isReady, setIsReady] = useState(false)
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: ROWS_PER_PAGE })
  const { page, rowsPerPage } = pagination

  const { watch } = useFormContext()
  const selectedServices = watch('selectedServices')
  const servicesOptions = watch('servicesOptions')
  const selectedRoutes = watch('selectedRoutes')
  const routesOptions = watch('routesOptions')
  const selectedWorkOrderStatuses = watch('selectedWorkOrderStatuses')
  const selectedDateRange = watch('selectedDateRange')

  const isAnySelecedFilterEmpty = useMemo(
    () => (hasFilters ? selectedServices.length === 0 || selectedRoutes.length === 0 || selectedWorkOrderStatuses.length === 0 : false),
    [hasFilters, selectedServices, selectedRoutes, selectedWorkOrderStatuses]
  )

  const totalCount = useMemo(() => (isAnySelecedFilterEmpty ? 0 : get(data, 'totalItems', 0)), [data, isAnySelecedFilterEmpty])
  const totalPages = useMemo(() => (isAnySelecedFilterEmpty ? 0 : get(data, 'totalPages', 0)), [data, isAnySelecedFilterEmpty])
  const workOrders = useMemo(() => (isAnySelecedFilterEmpty ? [] : get(data, 'workOrders', [])), [data, isAnySelecedFilterEmpty])
  const hasWorkOrders = useMemo(() => workOrders.length > 0, [workOrders])
  const showPagination = useMemo(() => totalPages > 1, [totalPages])

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleRowsPerPageChange = event => setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })

  const getFilters = () =>
    hasFilters
      ? buildWorkOrderListFilters({ selectedServices, servicesOptions, selectedRoutes, routesOptions, selectedWorkOrderStatuses })
      : {}

  const getWorkOrderList = (filters = {}) => {
    const monthNumber = MONTHS.findIndex(m => m === month)
    const monthStart = new Date(year, monthNumber, 1)
    const monthEnd = new Date(year, monthNumber + 1, 0)

    let effectiveStartDate = monthStart
    let effectiveEndDate = monthEnd

    if (hasFilters && selectedDateRange?.[0]) {
      const rangeStart = selectedDateRange[0]
      const rangeEnd = selectedDateRange[1] || rangeStart

      // Use the later of month start or range start
      if (rangeStart > monthStart) {
        effectiveStartDate = rangeStart
      }
      // Use the earlier of month end or range end
      if (rangeEnd < monthEnd) {
        effectiveEndDate = rangeEnd
      }
    }

    fetchWorkOrderList({
      accountId,
      ...(locationId && { locationId }),
      ...(configuredServiceId && { configuredServiceIds: [configuredServiceId] }),
      requestedPage: page,
      requestedPageSize: rowsPerPage,
      startDate: formatDateToBEFormat(effectiveStartDate),
      endDate: formatDateToBEFormat(effectiveEndDate),
      sortRules: [...DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC],
      ...filters,
    })
      .unwrap()
      .then(() => setIsReady(true))
      .catch(handleError)
  }

  useEffect(() => {
    if (isExpanded && isReady) {
      getWorkOrderList({ ...getFilters() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paging only; the rest are per-render identities or guards, and would refetch on renders that changed no page
  }, [page, rowsPerPage])

  useEffect(() => {
    if (isExpanded && !hasFilters && configuredServiceId) {
      getWorkOrderList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getWorkOrderList is redeclared every render
  }, [isExpanded, hasFilters, configuredServiceId])

  useEffect(() => {
    if (isExpanded && hasFilters && !isAnySelecedFilterEmpty) {
      if (page === INITIAL_PAGE) {
        getWorkOrderList({ ...getFilters(), page: INITIAL_PAGE })
      } else {
        handlePageChange()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection changes only; page is read to choose refetch-vs-reset and would double-fetch with the paging effect
  }, [isExpanded, hasFilters, isAnySelecedFilterEmpty, selectedWorkOrderStatuses, selectedServices, selectedRoutes, selectedDateRange])

  useEffect(() => {
    if (totalCount && !hasWorkOrders) {
      handlePageChange()
    }
  }, [totalCount, hasWorkOrders])

  return (
    <>
      {isLoading && <LinearProgress sx={{ height: 3 }} />}

      {!isLoading && !hasWorkOrders && <HHSectionPlaceholder title="No work orders found" />}

      {hasWorkOrders &&
        Children.toArray(
          workOrders.map((workOrder, index) => (
            <WorkOrderListItem
              showDisposalTicket
              accountId={accountId}
              disposalSites={disposalSites}
              showDivider={index !== workOrders.length - 1}
              workOrder={workOrder}
            />
          ))
        )}

      {isLoading && <LinearProgress sx={{ height: 3 }} />}

      {showPagination && (
        <>
          <Divider />
          <Box alignItems="center" backgroundColor="background.header" display="flex" justifyContent="space-between" px={3}>
            <HHTablePagination
              ActionsComponent={params => <Box display="none" {...params} />}
              component="div"
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onPageChange={(event, newPage) => handlePageChange(newPage)}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            <Pagination color="primary" count={totalPages} page={page + 1} onChange={(event, newPage) => handlePageChange(newPage - 1)} />
          </Box>
        </>
      )}
    </>
  )
}

WorkOrderList.propTypes = {
  accountId: PropTypes.string,
  locationId: PropTypes.string,
  configuredServiceId: PropTypes.string,
  isExpanded: PropTypes.bool,
  year: PropTypes.string,
  month: PropTypes.string,
  hasFilters: PropTypes.bool,
  disposalSites: PropTypes.array,
}

export default memo(WorkOrderList)
